import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Camera, Loader2, X, Filter, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FILTERS = [
  { id: 'all', label: 'All', match: () => true },
  { id: 'goat', label: 'Goats', match: (p) => p.animal_type === 'goat' },
  { id: 'sheep', label: 'Sheep', match: (p) => p.animal_type === 'sheep' },
  { id: 'cattle', label: 'Cattle', match: (p) => p.animal_type === 'cattle' },
  { id: 'poultry', label: 'Poultry', match: (p) => p.animal_type === 'poultry' },
  { id: 'profile', label: 'Profiles', match: (p) => p.source === 'profile' },
  { id: 'cover', label: 'Covers', match: (p) => p.source === 'cover' },
];

/**
 * Global photo gallery — aggregates every photo the user has uploaded across
 * the entire app (animal profile photos, herd/flock covers, per-animal gallery
 * entries).
 */
const Gallery = () => {
  const { getAuthHeader } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/animal-photos/gallery`, {
          headers: getAuthHeader(),
        });
        if (!cancelled) setPhotos(res.data.photos || []);
      } catch (e) {
        console.error('Failed to load gallery:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeader]);

  const filtered = useMemo(() => {
    const matcher = FILTERS.find((f) => f.id === filter)?.match || (() => true);
    return photos.filter(matcher);
  }, [photos, filter]);

  const handleDelete = async (photo) => {
    if (!window.confirm(`Delete this photo${photo.animal_name ? ` of ${photo.animal_name}` : ''}?`)) return;
    try {
      await axios.post(`${API_URL}/api/animal-photos/gallery-delete`, {
        source: photo.source,
        animal_id: photo.animal_id,
        animal_kind: photo.animal_kind,
        animal_type: photo.animal_type,
        photo_id: photo.source === 'gallery' ? photo.id : undefined,
        photo_url: photo.photo_url,
      }, { headers: getAuthHeader() });
      // Remove from local state using the composite key the grid uses
      setPhotos((prev) => prev.filter((p) => !(p.id === photo.id && p.source === photo.source)));
      setLightboxIndex(null);
      toast.success('Photo deleted');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="gallery-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Gallery</h1>
          <p className="text-sm text-muted-foreground">
            Every photo you&apos;ve uploaded across your farm.
          </p>
        </div>
        <div className="text-xs text-muted-foreground">
          {filtered.length} {filtered.length === 1 ? 'photo' : 'photos'}
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <Filter className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        {FILTERS.map((f) => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`text-xs px-3 py-1 rounded-full border transition-colors whitespace-nowrap ${
                active
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background border-muted-foreground/30 hover:border-primary/50'
              }`}
              data-testid={`gallery-filter-${f.id}`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="card-farm">
          <CardContent className="py-12 text-center">
            <Camera className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
            <h3 className="font-medium text-lg mb-2">No photos yet</h3>
            <p className="text-muted-foreground text-sm">
              {filter === 'all'
                ? 'Upload photos from any animal card and they will show up here.'
                : 'No photos match this filter. Try "All" or a different category.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
          {filtered.map((p, idx) => (
            <button
              key={`${p.source}-${p.id}`}
              onClick={() => setLightboxIndex(idx)}
              className="relative aspect-square rounded-lg overflow-hidden border bg-muted hover:opacity-90 transition-opacity group"
              data-testid={`gallery-thumb-${p.id}`}
            >
              <img
                src={p.photo_url}
                alt={p.caption || p.animal_name || ''}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 p-1.5 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">
                {p.animal_name}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={lightboxIndex !== null} onOpenChange={(o) => !o && setLightboxIndex(null)}>
        <DialogContent className="max-w-3xl p-0 bg-black/95 border-0">
          <DialogTitle className="sr-only">Photo preview</DialogTitle>
          {lightboxIndex !== null && filtered[lightboxIndex] && (
            <div className="relative">
              <button
                onClick={() => setLightboxIndex(null)}
                className="absolute top-2 right-2 z-10 p-2 rounded-full bg-black/60 text-white hover:bg-black/80"
                data-testid="gallery-close-lightbox"
              >
                <X className="w-4 h-4" />
              </button>
              <img
                src={filtered[lightboxIndex].photo_url}
                alt={filtered[lightboxIndex].caption || ''}
                className="w-full max-h-[80vh] object-contain bg-black"
              />
              <div className="p-3 bg-black/80 text-white text-xs">
                <div className="font-medium">
                  {filtered[lightboxIndex].animal_name || 'Untitled'}
                </div>
                <div className="opacity-70">
                  {filtered[lightboxIndex].caption}
                  {filtered[lightboxIndex].created_at && (
                    <span className="ml-2">
                      · {new Date(filtered[lightboxIndex].created_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={lightboxIndex === 0}
                    onClick={() => setLightboxIndex((i) => Math.max(0, (i || 0) - 1))}
                  >
                    Prev
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={lightboxIndex >= filtered.length - 1}
                    onClick={() => setLightboxIndex((i) => Math.min(filtered.length - 1, (i || 0) + 1))}
                  >
                    Next
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(filtered[lightboxIndex])}
                    data-testid="gallery-delete-photo"
                    className="ml-auto"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Gallery;
